import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Login, { SSOLookupMode } from './Login';
import { ForgotPassword } from './../auth-components/ForgotPassword';
import { MFAEmailChallenge } from './../auth-components/MFAEmailChallenge';
import { MFATOTPChallenge } from './../auth-components/MFATOTPChallenge';
import { MFASetup } from './../auth-components/MFASetup';
import { authActions } from '../../../state/features/authReducer';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../state/features/reducers';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from '@reduxjs/toolkit';
import { decodeToken } from '../../../utils/tokenUtils';

type FormMode = 'LOGIN' | 'FORGOT_PASSWORD' | 'CREATE_NEW_PASSWORD' | 'SOFTWARE_TOKEN_MFA' | 'MFA_SETUP' | 'SMS_MFA' | 'EMAIL_OTP' | 'SMS_OTP' | 'EMAIL_MFA' | 'SELECT_CHALLENGE';

const LoginContainer: React.FC = () => {
    const { t } = useTranslation();
    const [username, setUserName] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [mfaCode, setMfaCode] = useState<string>('');
    const [passwordErrMsg, setPasswordErrMsg] = useState<string>('');
    const [formMode, setFormMode] = useState<FormMode>('LOGIN');
    const [showSSOForm, setShowSSOForm] = useState<boolean>(false);
    const [tenantId, setTenantId] = useState<string>('');
    const [domain, setDomain] = useState<string>('');
    const [ssoLookupMode, setSSOLookupMode] = useState<SSOLookupMode>('domain');

    const passwordMismatchErrMsg = 'Passwords must match.';

    const authChallenge = useSelector((x: RootState) => x.auth?.authChallengeData);
    const mfaChallenge = useSelector((x: RootState) => x.auth?.mfaChallengeData);
    const mfaSetupData = useSelector((x: RootState) => x.auth?.mfaSetupData);
    const user = useSelector((x: RootState) => x.auth?.user);
    const errorMessage = useSelector((x: RootState) => x.auth.error);
    const authLoading = useSelector((x: RootState) => x?.auth?.loading);
    const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();

    const onSubmit = useCallback(async () => {
        if (username.length > 1 && password.length > 1) {
            if (Boolean(authChallenge)) {
                // Handle password challenge
                if (password === confirmPassword) {
                    onRespondToNewPassword();
                } else {
                    setPasswordErrMsg(passwordMismatchErrMsg);
                }
            } else if (Boolean(mfaChallenge)) {
                // Handle MFA challenge
                if (mfaCode.length > 0) {
                    onRespondToMFA();
                }
            } else {
                // Regular login- Setting username to lowercase and trimming spaces from password-
                dispatch(authActions.login({
                    username: username.toLowerCase(),
                    password: password.replace(/\s+/g, '')
                }));
            }
        } else if (formMode === 'EMAIL_OTP' && mfaCode.length > 0) {
            // MFA code submission without username/password
            onRespondToMFA();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username, password, confirmPassword, mfaCode, authChallenge, mfaChallenge, formMode]);

    useEffect(() => {
        if (authChallenge) {
            setPassword('');
            setConfirmPassword('');
            if (authChallenge.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
                setFormMode('CREATE_NEW_PASSWORD');
            } else {
                if (authChallenge.ChallengeName === 'EMAIL_OTP') {
                    setFormMode('EMAIL_OTP');
                } else if (authChallenge.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
                    setFormMode('SOFTWARE_TOKEN_MFA');
                } else if (authChallenge.ChallengeName === 'SMS_MFA') {
                    setFormMode('SMS_MFA');
                }
            }
        } else if (!authChallenge && formMode === 'CREATE_NEW_PASSWORD') {
            // Reset to login form when challenge is cleared (successful response)
            setFormMode('LOGIN');
            setPassword('');
            setConfirmPassword('');
        } else if (!authChallenge && !mfaChallenge) {
            setFormMode('LOGIN');
        }
    }, [authChallenge, formMode]);

    useEffect(() => {
        if (mfaChallenge) {
            setMfaCode('');
            // Determine MFA type based on challenge name
            if (mfaChallenge.ChallengeName === 'EMAIL_OTP') {
                setFormMode('EMAIL_OTP');
            } else if (mfaChallenge.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
                setFormMode('SOFTWARE_TOKEN_MFA');
            } else if (mfaChallenge.ChallengeName === 'SMS_MFA') {
                setFormMode('SMS_MFA');
            }
        }
    }, [mfaChallenge]);

    useEffect(() => {
        if (mfaSetupData) {
            setFormMode('MFA_SETUP');
        }
    }, [mfaSetupData]);

    const ssoEnabled = Boolean(process.env.REACT_APP_SSO_PROVIDERS);

    const clickGate = Boolean(authLoading) && !authChallenge && !mfaChallenge;

    const getUserEmail = () => {
        if (user?.idToken) {
            const decodedData = decodeToken(user.idToken);
            return decodedData?.email || username;
        }
        return username;
    };

    function onForgotPassword() {
        setFormMode('FORGOT_PASSWORD');
    }

    async function onRespondToNewPassword() {
        if (authChallenge?.Session) {
            dispatch(authActions.respondToChallenge({
                username,
                password,
                sessionToken: authChallenge.Session
            }));  
        }
        
    }

    async function onRespondToMFA() {
        if (mfaChallenge?.Session) {
            dispatch(authActions.respondToEmailMFA({
                username: mfaChallenge.username,
                sessionToken: mfaChallenge.Session,
                emailCode: mfaCode
            }));
        }
    }


    const toDisplayString = (): string => {
        if (formMode === 'LOGIN') return 'Login';
        if (formMode === 'CREATE_NEW_PASSWORD') return 'Create New Password';
        if (formMode === 'EMAIL_OTP') return 'Email Verification';
        if (formMode === 'SOFTWARE_TOKEN_MFA') return 'Authenticator Verification';
        if (formMode === 'MFA_SETUP') return 'Setup Multi-Factor Authentication';
        return 'Liveline';
    };

    const onChangePassword = (value: string) => {
        setPassword(value);
        setPasswordErrMsg('');
    };

    const onChangeConfirmPassword = (value: string) => {
        setConfirmPassword(value);
        setPasswordErrMsg('');
    };

    const onMfaCodeChange = (value: string) => {
        setMfaCode(value);
    };

    const title = toDisplayString();

    const onUserNameChange = (value: string) => {
        setUserName(value);
    };

    const onFormModeChange = (formMode: FormMode) => {
        setFormMode(formMode);
    };

    // Clear MFA challenge when switching back to login
    const handleBackToLogin = () => {
        dispatch(authActions.clearMFAChallenge());
        dispatch(authActions.clearMFASetup());
        setFormMode('LOGIN');
        setMfaCode('');
    };

    // Handle MFA setup completion
    const handleMFASetupComplete = () => {
        dispatch(authActions.clearMFASetup());
        setFormMode('LOGIN');
    };

    // Render appropriate component based on form mode
    if (formMode === 'FORGOT_PASSWORD') {
        return (
            <ForgotPassword
                onComplete={() => onFormModeChange('LOGIN')}
            />
        );
    }

    if (formMode === 'EMAIL_OTP' && mfaChallenge) {
        return (
            <MFAEmailChallenge
                onComplete={handleBackToLogin}
                onCancel={handleBackToLogin}
                mfaChallenge={mfaChallenge}
            />
        );
    }

    if (formMode === 'SOFTWARE_TOKEN_MFA' && mfaChallenge) {
        return (
            <MFATOTPChallenge
                onComplete={handleBackToLogin}
                onCancel={handleBackToLogin}
                username={mfaChallenge.username}
                sessionToken={mfaChallenge.Session}
            />
        );
    }

    if (formMode === 'SMS_MFA' && mfaChallenge) {
        return (
            <MFATOTPChallenge
                onComplete={handleBackToLogin}
                onCancel={handleBackToLogin}
                username={mfaChallenge.username}
                sessionToken={mfaChallenge.Session}
            />
        );
    }

    if (formMode === 'MFA_SETUP' && user) {
        return (
            <MFASetup
                onComplete={handleMFASetupComplete}
                onCancel={handleBackToLogin}
                username={getUserEmail()}
                accessToken={user.accessToken || ''}
                setupType={'totp'}
            />
        );
    }

    

    const handleShowSSOForm = () => {
        dispatch(authActions.clearError());
        setShowSSOForm(true);
    };

    const handleBackFromSSO = () => {
        setShowSSOForm(false);
        setTenantId('');
        setDomain('');
        setSSOLookupMode('domain');
        dispatch(authActions.clearError());
    };

    const handleSSOLogin = (params: { domain?: string; tenantId?: string }) => {
        dispatch(authActions.initiateSSOLogin(params));
    };

    // Default login form
    return (
        <Login
            formMode={formMode as 'LOGIN' | 'CREATE_NEW_PASSWORD'}
            title={title}
            errorMessage={errorMessage || ''}
            passwordErrMsg={passwordErrMsg}
            username={username}
            clickGate={clickGate}
            onUserNameChange={onUserNameChange}
            password={password}
            onChangePassword={onChangePassword}
            confirmPassword={confirmPassword}
            onChangeConfirmPassword={onChangeConfirmPassword}
            onForgotPassword={onForgotPassword}
            onSubmit={onSubmit}
            ssoEnabled={ssoEnabled}
            onShowSSOForm={ssoEnabled ? handleShowSSOForm : undefined}
            showSSOForm={showSSOForm}
            onBackFromSSO={handleBackFromSSO}
            tenantId={tenantId}
            onTenantIdChange={setTenantId}
            domain={domain}
            onDomainChange={setDomain}
            ssoLookupMode={ssoLookupMode}
            onSSOLookupModeChange={setSSOLookupMode}
            onSSOLogin={ssoEnabled ? handleSSOLogin : undefined}
        />
    );
};

export default LoginContainer;
